import { db, fieldValue } from "../firebase/firebase.utils";
const activeDocument = (uid) => db.collection("users").doc(uid).collection("meta").doc("activeFocus");
const newLogDocument = (uid) => db.collection("users").doc(uid).collection("timelogs").doc();
const atomic = (fn) => db.runTransaction(fn);
const timestamp = () => fieldValue.serverTimestamp();
export const watchFocus = (uid, cb, onError) => activeDocument(uid).onSnapshot({ includeMetadataChanges: true },
  (snap) => cb(normalizeFocus(snap.data(), uid), !snap.metadata.fromCache), onError);

// Identical session rules in the mobile and web helpers; only Firebase imports differ.
export const FOCUS_DEFAULTS = Object.freeze({
  autoBreak: true, autoFocus: false, sound: true, reminders: true,
  haptics: false, keepAwake: false, cycleLimit: 0,
});
const clamp = (n, min, max) => Math.max(min, Math.min(max, n));
export function toMillis(value) {
  if (value == null) return null;
  const n = typeof value.toMillis === "function" ? value.toMillis()
    : typeof value.toDate === "function" ? value.toDate().getTime()
    : typeof value === "number" ? value : new Date(value).getTime();
  return Number.isFinite(n) ? Math.round(n) : null;
}
const positiveSeconds = (value, fallback) => Number(value) > 0 && Number.isFinite(Number(value)) ? Number(value) : fallback;
export function timeStringToSeconds(value) {
  if (typeof value !== "string" || !value.trim()) return null;
  const parts = value.split(":").map(Number);
  if (parts.some((n) => !Number.isFinite(n) || n < 0) || parts.length > 2) return null;
  const n = parts.length === 1 ? parts[0] * 60 : (parts[0] * 60 + parts[1]) * 60;
  return n > 0 ? clamp(Math.round(n), 60, 14400) : null;
}
export function taskReference(task, uid) {
  if (!task?.id) return null;
  const habit = task.type === "habit" || task.fromSpaceId === `${uid}-Habits` ||
    (!task.title && !!task.name);
  return habit
    ? { type: "habit", id: task.id, name: task.name || "Habit", icon: task.icon || "" }
    : { type: "task", id: task.id, title: task.title || "Untitled",
        fromSpaceId: task.fromSpaceId || `${uid}-Inbox`, fromStationId: task.fromStationId || null,
        icon: task.focusIcon || task.icon || "" };
}
export const phaseDuration = (a) => a?.phaseSeconds ??
  (a?.mode === "break" ? a?.breakMs ?? 300 : a?.sessionMs ?? 1500);
export function remainingSeconds(a, now = Date.now()) {
  if (!a) return 1500;
  const total = phaseDuration(a);
  if (a.state === "running") return clamp(((toMillis(a.targetEndAt) ?? now) - now) / 1000, 0, total);
  return a.state === "paused" ? clamp(a.remainingMs ?? total, 0, total) : total;
}
export function formatFocusTime(seconds) {
  const n = Math.max(0, Math.ceil(seconds));
  return `${Math.floor(n / 60).toString().padStart(2, "0")}:${(n % 60).toString().padStart(2, "0")}`;
}
// Only automatic completion needs an identity check. Settings and map key order
// do not change which running phase is due to finish.
export function completionKey(a) {
  return JSON.stringify([a?.phaseId || null, a?.mode || "focus", toMillis(a?.targetEndAt)]);
}
// Kept for existing imports; manual commands no longer require a version.
export const focusVersion = completionKey;

export function normalizeFocus(raw, uid) {
  if (!raw) return null;
  return {
    ...raw, uid,
    state: ["running", "paused", "stopped"].includes(raw.state) ? raw.state : "stopped",
    mode: raw.mode === "break" ? "break" : "focus",
    targetEndAt: toMillis(raw.targetEndAt),
    startedAt: toMillis(raw.startedAt), updatedAt: toMillis(raw.updatedAt),
    lastSeenAt: toMillis(raw.lastSeenAt),
    sessionMs: positiveSeconds(raw.sessionMs, 1500), breakMs: positiveSeconds(raw.breakMs, 300),
    phaseSeconds: positiveSeconds(raw.phaseSeconds, raw.mode === "break"
      ? positiveSeconds(raw.breakMs, 300) : positiveSeconds(raw.sessionMs, 1500)),
    remainingMs: raw.remainingMs == null ? null : Math.max(0, Number(raw.remainingMs) || 0),
    options: { ...FOCUS_DEFAULTS, ...raw.options },
  };
}

// Pure transition: no side effects, even when Firestore retries a transaction.
export function transitionFocus(raw, command, payload, now, id, uid) {
  const a = normalizeFocus(raw, uid) || {
    state: "stopped", mode: "focus", sessionMs: 1500, breakMs: 300,
    taskRef: null, options: { ...FOCUS_DEFAULTS },
  };
  const total = phaseDuration(a);
  const remaining = remainingSeconds(a, now);
  const elapsed = Math.max(0, Math.floor(total - remaining + 0.000001));
  // Legacy paused sessions already wrote their elapsed time. For a legacy run,
  // only its latest startedAt -> targetEndAt segment can be safely accounted for.
  const legacyBaseline = a.state === "paused" ? elapsed
    : a.state === "running" && a.startedAt && a.targetEndAt
      ? Math.max(0, Math.floor(total - (a.targetEndAt - a.startedAt) / 1000)) : 0;
  const logged = a.engineVersion === 2 ? (a.loggedSeconds || 0) : legacyBaseline;
  let next = { ...a, engineVersion: 2, revision: id, phaseId: a.phaseId || id,
    phaseSeconds: total, loggedSeconds: logged };
  let log = null;
  const settle = () => {
    if (a.state !== "running") return;
    const delta = Math.max(0, elapsed - logged);
    next.loggedSeconds = elapsed;
    if (a.mode !== "break" && a.taskRef && delta > 0) {
      const t = a.taskRef;
      log = { billable: false, duration: delta, loggedBy: uid,
        logTime: new Date(Math.min(now, a.targetEndAt ?? now)),
        ...(t.type === "habit"
          ? { fromSpaceId: `${uid}-Habits`, fromStationId: t.name, habitId: t.id }
          : { fromSpaceId: t.fromSpaceId || `${uid}-Inbox`, fromStationId: t.fromStationId || null, taskId: t.id }) };
    }
  };
  const ready = (mode = next.mode) => {
    next = { ...next, mode, state: "paused", phaseId: id,
      phaseSeconds: mode === "break" ? next.breakMs : next.sessionMs,
      remainingMs: mode === "break" ? next.breakMs : next.sessionMs,
      targetEndAt: null, startedAt: null, loggedSeconds: 0 };
  };
  const start = () => {
    const r = next.remainingMs > 0 ? next.remainingMs : phaseDuration(next);
    next.state = "running";
    next.targetEndAt = now + r * 1000;
    next.startedAt = now;
    next.remainingMs = null;
  };
  switch (command) {
    case "start":
      if (a.state === "running" && remaining > 0) return null;
      if (a.state === "running") settle();
      if (a.state !== "paused" || remaining <= 0) ready();
      else next.remainingMs = remaining;
      start();
      break;
    case "pause":
      if (a.state !== "running") return null;
      settle();
      next.state = "paused"; next.remainingMs = remaining; next.targetEndAt = null;
      break;
    case "reset":
      settle(); ready(); next.cyclesCompleted = 0;
      break;
    case "mode":
      if (payload.mode === a.mode) return null;
      settle(); ready(payload.mode === "break" ? "break" : "focus");
      break;
    case "select": {
      const selected = taskReference(payload.task, uid);
      const oldTask = a.taskRef;
      const sameTask = selected?.type === oldTask?.type && selected?.id === oldTask?.id &&
        (selected?.fromSpaceId || null) === (oldTask?.fromSpaceId || null) &&
        (selected?.fromStationId || null) === (oldTask?.fromStationId || null);
      const selectedDuration = timeStringToSeconds(payload.task?.duration);
      if (selected && sameTask && (a.state === "running" || !selectedDuration || selectedDuration === a.sessionMs)) {
        // Refresh a renamed task without restarting its phase.
        if (!selected || (selected.title === oldTask.title && selected.name === oldTask.name && selected.icon === oldTask.icon)) return null;
        next.taskRef = selected;
        break;
      }
      settle(); next.taskRef = selected; next.cyclesCompleted = 0;
      next.sessionMs = timeStringToSeconds(payload.task?.duration) || next.sessionMs;
      ready("focus");
      break;
    }
    case "length": {
      if (a.state === "running") return null;
      const field = payload.mode === "break" ? "breakMs" : "sessionMs";
      if (!Number.isFinite(payload.delta)) return null;
      next[field] = clamp(next[field] + payload.delta, 60, field === "breakMs" ? 3600 : 14400);
      if (next[field] === a[field]) return null;
      if (payload.mode === a.mode) ready();
      break;
    }
    case "options": {
      let changed = false;
      for (const key of Object.keys(FOCUS_DEFAULTS)) {
        if (!(key in payload)) continue;
        const value = key === "cycleLimit" ? clamp(Math.round(Number(payload[key]) || 0), 0, 12) : !!payload[key];
        if (next.options[key] !== value) { next.options = { ...next.options, [key]: value }; changed = true; }
      }
      if (!changed) return null;
      break;
    }
    case "complete": {
      if (a.state !== "running" || remaining > 0) return null;
      settle();
      const late = now - a.targetEndAt > 10000;
      const cycles = (a.cyclesCompleted || 0) + (a.mode === "focus" ? 1 : 0);
      next.cyclesCompleted = cycles;
      ready(a.mode === "break" ? "focus" : "break");
      const permitted = next.mode === "break" ? next.options.autoBreak
        : next.options.autoFocus && (!next.options.cycleLimit || cycles < next.options.cycleLimit);
      if (permitted && !late) start();
      next.event = { id, kind: "complete", mode: next.mode, at: now, late };
      break;
    }
    default: return null;
  }
  if (command === "start") next.event = { id, kind: "start", mode: next.mode, at: now, late: false };
  // Do not write the uid injected for Redux, or stale server metadata.
  delete next.uid; delete next.updatedAt; delete next.lastSeenAt;
  return { next, log };
}

export async function changeFocus(uid, command, payload = {}, expectedPhase = undefined) {
  if (!uid) throw new Error("Sign in to use the focus timer.");
  const activeRef = activeDocument(uid);
  const logRef = newLogDocument(uid);
  const id = logRef.id; // Stable for all retries of this command.
  return atomic(async (tx) => {
    const snap = await tx.get(activeRef);
    const raw = snap.data();
    // Read fresh state for every user action. Firestore itself retries conflicts.
    // A stale automatic callback simply returns current state to its caller.
    if (command === "complete" && expectedPhase !== undefined && completionKey(raw) !== expectedPhase) {
      return { changed: false, next: normalizeFocus(raw, uid) };
    }
    const now = Date.now();
    const result = transitionFocus(raw, command, payload, now, id, uid);
    if (!result) return { changed: false, next: normalizeFocus(raw, uid) };
    const { next, log } = result;
    if (log) tx.set(logRef, log);
    tx.set(activeRef, { ...next, targetEndAt: next.targetEndAt == null ? null : new Date(next.targetEndAt),
      startedAt: next.startedAt == null ? null : new Date(next.startedAt),
      updatedAt: timestamp(), lastSeenAt: timestamp() }, { merge: true });
    return { changed: true, next: normalizeFocus(next, uid) };
  });
}

// Render immediately, then save commands in order. This is an in-memory UI queue,
// not offline persistence. Failed commands roll back to confirmed state.
export function createFocusCommands({ uid, read, commit, preview, error, send = changeFocus }) {
  const jobs = [];
  let base = null, saving = false, attached = true, debounce;
  const show = () => {
    if (!attached) return;
    let draft = base;
    for (const job of jobs) {
      const result = transitionFocus(draft, job.command, job.payload, job.at, job.id, uid);
      if (result) draft = normalizeFocus(result.next, uid);
    }
    preview(jobs.length ? { value: draft, pending: jobs.length } : null);
  };
  const drain = async () => {
    clearTimeout(debounce);
    if (saving) return;
    saving = true;
    while (jobs.length) {
      const job = jobs[0];
      const before = read();
      try {
        const result = await send(uid, job.command, job.payload, job.expected);
        commit(result.next, before);
        base = read();
        jobs.shift(); show();
        job.resolve(true);
      } catch (e) {
        jobs.shift();
        base = read(); show();
        if (attached) error(`Could not save that change. ${e.message || "Please reconnect and try again."}`);
        job.resolve(false);
      }
    }
    saving = false;
  };
  return {
    run(command, payload = {}) {
      if (!jobs.length) { base = read(); if (attached) error(null); }
      return new Promise((resolve) => {
        const last = jobs[jobs.length - 1];
        if (command === "length" && last?.command === "length" && (!saving || jobs.length > 1) &&
            last.payload.mode === payload.mode && Math.sign(last.payload.delta) === Math.sign(payload.delta)) {
          last.payload.delta += payload.delta;
          const previous = last.resolve;
          last.resolve = (ok) => { previous(ok); resolve(ok); };
        } else jobs.push({ command, payload: { ...payload }, resolve, at: Date.now(),
          id: `preview-${Date.now()}-${Math.random().toString(36).slice(2)}`,
          expected: command === "complete" ? completionKey(base) : undefined });
        show();
        if (command === "length" && !saving) { clearTimeout(debounce); debounce = setTimeout(drain, 300); }
        else void drain();
      });
    },
    attach() { attached = true; },
    detach() { attached = false; },
  };
}

// Uses already loaded project data. No database reads are added for an emoji.
export function focusEmoji(task, stations = []) {
  if (!task) return null;
  if (task.type === "habit" || (!task.title && task.name)) return task.icon || null;
  if (!task.fromStationId || task.fromSpaceId?.endsWith("-Inbox")) return null;
  const matches = stations.filter((s) => s && (s.stationId || s.id) === task.fromStationId);
  const station = matches.find((s) => (s.spaceId || s.fromSpaceId) === task.fromSpaceId)
    || (matches.length === 1 ? matches[0] : null);
  return station?.emojicon || task.icon || null;
}
